import { useState, useRef, useEffect, useCallback } from 'react';
import { generateApi } from '../services/api';
import { useStore } from '../store';
import {
  X,
  HelpCircle,
  Send,
  Loader2,
  Trash2,
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface HelpMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const SUGGESTED_QUESTIONS = [
  'How do I create a new document section?',
  'How can I export my document as PDF?',
  'How do I connect cloud storage?',
  'What AI models are available?',
  'How do I use templates?',
  'How can I regenerate specific text?',
];

export default function HelpAssistant({ isOpen, onClose }: Props) {
  const { applicantData } = useStore();
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: HelpMessage = {
      id: `help-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const helpPrompt = `You are a helpful assistant for the DocWriter application. DocWriter is a document creation tool that helps users generate professional documents using AI.

Key features of DocWriter:
- Create documents with multiple sections
- AI-powered content generation using OpenAI, Anthropic, or Google Gemini
- Template management for reusable document structures
- Cloud storage integration (Google Drive, Dropbox, OneDrive)
- Export to PDF, DOCX, and HTML
- Session management for saving and loading documents
- Text selection tools for regenerating specific parts
- Customizable system prompts for AI generation

User question: ${content.trim()}

Provide a clear, helpful, and concise answer about how to use DocWriter.`;

      const response = await generateApi.generate({
        section: {
          id: 0,
          title: 'Help Assistant',
          prompt: helpPrompt,
        },
        applicantData,
        systemPrompt: 'You are a helpful documentation assistant. Provide clear, step-by-step answers about how to use the DocWriter application. Keep responses concise and actionable.',
        llmModel: applicantData.llmModel,
      });

      const assistantMessage: HelpMessage = {
        id: `help-${Date.now() + 1}`,
        role: 'assistant',
        content: response.content || response.generatedContent || 'I apologize, I could not generate a response. Please try again.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: any) {
      const errorMessage: HelpMessage = {
        id: `help-${Date.now() + 1}`,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${err.response?.data?.error || 'Unable to get response. Please check your API key settings and try again.'}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const clearHistory = () => {
    setMessages([]);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content max-w-lg h-[600px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <HelpCircle size={20} className="text-accent" />
            <h2 className="text-lg font-semibold">Help Assistant</h2>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                onClick={clearHistory}
                className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear history"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <HelpCircle size={40} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">
                  Ask me anything about DocWriter!
                </p>
              </div>

              {/* Suggested Questions */}
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                  Suggested Questions
                </p>
                <div className="space-y-1.5">
                  {SUGGESTED_QUESTIONS.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => sendMessage(question)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-600 rounded-lg border border-gray-100 hover:bg-gray-50 hover:border-gray-200 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-accent text-white rounded-br-sm'
                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}
              >
                <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
                <p
                  className={`text-[10px] mt-1 ${
                    msg.role === 'user' ? 'text-white/60' : 'text-gray-400'
                  }`}
                >
                  {formatTime(msg.timestamp)}
                </p>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-3 py-2 rounded-bl-sm">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Loader2 size={14} className="animate-spin" />
                  <span>Looking up the answer...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 shrink-0">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about DocWriter..."
              rows={1}
              className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent max-h-20"
              style={{ minHeight: '38px' }}
              disabled={isLoading}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || isLoading}
              className="p-2.5 rounded-lg bg-accent text-white hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
